import { Injectable } from '@angular/core';
import { AlertController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  presentingError: boolean;
  presentingJavascriptError: boolean;

  constructor(
    public translate: TranslateService,
    private alertController: AlertController,
    public platform: Platform
  ) { 
    this.presentingError = false;
    this.presentingJavascriptError = false;
  }

  async infoAlert(message, closeText = null) {
    let buttonText = closeText ? closeText : this.translate.instant("Close");
    if(!this.presentingError) {
      this.presentingError = true;
      const alert = await this.alertController.create({
        cssClass: "alert-box",
        message: `<img src="assets/info.svg" class="alert-header-image"/><br>` + `<ion-label class="alert-header-text">${this.translate.instant("ERROR.attention")}</ion-label>` + `<br><ion-label style="margin: 25px">${message}</ion-label>`,
        buttons: [
          {
            text: buttonText,
            role: 'yes',
            cssClass: 'info-button',
            id: 'cancel-button'
          }
        ]
      });
  
      await alert.present();
      await alert.onWillDismiss().then(() => {
        this.presentingError = false;
      });
      
      return alert.onDidDismiss();
    }
  }

  async errorAlert(errMessage) {
    if(!this.presentingError) {
      this.presentingError = true;
      const alert = await this.alertController.create({
        cssClass: "alert-box",
        message: `<img src="assets/error.svg" class="alert-header-image"/>` + `<ion-label class="alert-header-text">${this.translate.instant("ERROR.somethingWrong")}</ion-label>` + `<br><ion-label>${errMessage}</ion-label>`,
        buttons: [
          {
            text: this.translate.instant("Close"),
            role: 'cancel',
            cssClass: 'error-button',
            id: 'cancel-button'
          }
        ]
      });
  
      await alert.present();
      await alert.onDidDismiss().then(() => {
        this.presentingError = false;
      });
    }
  }

  async warningAlert(message: string) {
    if(!this.presentingError) {
      this.presentingError = true;
      const alert = await this.alertController.create({
        cssClass: "alert-box",
        message: `<img src="assets/warning.svg" class="alert-header-image"/><br>` + `<ion-label class="alert-header-text">${this.translate.instant("ERROR.warning")}</ion-label>` + `<br><ion-label>${message}</ion-label>`,
        buttons: [
          {
            text: this.translate.instant("Close"),
            role: 'cancel',
            cssClass: 'warning-button',
            id: 'cancel-button'
          }
        ]
      });
  
      await alert.present();
      await alert.onDidDismiss().then(() => {
        this.presentingError = false;
      });
    }
  }

  async successAlert(message: string) {
    this.presentingError = true;
    const alert = await this.alertController.create({
      cssClass: "alert-box",
      message: `<img src="assets/success.svg" class="alert-header-image"/><br>` + `<ion-label class="alert-header-text">${this.translate.instant("ERROR.success")}</ion-label>` + `<br><ion-label>${message}</ion-label>`,
      buttons: [
        {
          text: this.translate.instant("Close"),
          role: 'cancel',
          cssClass: 'success-button',
          id: 'cancel-button'
        }
      ]
    });

    await alert.present();
    await alert.onDidDismiss().then(() => {
      this.presentingError = false;
    });
  }

  async inputErrorAlert(errMessage) {
    if(!this.presentingError) {
      this.presentingError = true;
      const alert = await this.alertController.create({
        cssClass: "alert-box",
        message: `<img src="assets/input_error.svg" class="alert-header-image"/><br>` + `<ion-label class="alert-header-text">${this.translate.instant("ERROR.whoops")}</ion-label>` + `<br><ion-label>${errMessage}</ion-label>`,
        buttons: [
          {
            text: this.translate.instant("Close"),
            role: 'cancel',
            cssClass: 'error-button',
            id: 'cancel-button'
          }
        ]
      });
  
      await alert.present();
      await alert.onDidDismiss().then(() => {
        this.presentingError = false;
      });
    }
  }

  async yesNoAlert(header: string, text: string) {
    const alert = await this.alertController.create({
      header: header,
      message: text,
      buttons: [
        {
          text: this.translate.instant("Yes"),
          id: 'yes-button',
          role: 'yes'
        },
        {
          text: this.translate.instant("No"),
          role: 'no',
          id: 'no-button'
        }
      ],
      cssClass: "alert-text"
    });

    await alert.present();

    return await alert.onDidDismiss();
  }

  async dangerAlert(header: string, text: string, yesText: string, noText: string) {
    const alert = await this.alertController.create({
      cssClass: "choice-alert-box",
      message: `<img src="assets/icons/alert.svg" class="alert-header-image"/><br>` + `<ion-label class="alert-header-text">${header}</ion-label>` + `<br><ion-label>${text}</ion-label>`,
      buttons: [
        {
          text: noText,
          role: 'no',
          cssClass: 'secondary',
          id: 'no-button'
        },
        {
          text: yesText,
          cssClass: 'alertDanger',
          id: 'yes-button',
          role: 'yes'
        },
      ]
    });

    await alert.present();

    return await alert.onDidDismiss();
  }

  async showSelectAll(header: string, options: any[]) {
    let buttons = [
      {
        text: this.translate.instant('All'),
        cssClass: 'all-none-button',
        handler: () => {

          // check all checkboxes
          alert.inputs = alert.inputs.map((checkbox) => {
            checkbox.checked = true;
            return checkbox;
          });

          return false;
        }
      }, {
        text: this.translate.instant('None'),
        cssClass: 'all-none-button',
        handler: () => {

          // uncheck all checkboxes
          alert.inputs = alert.inputs.map((checkbox) => {
            checkbox.checked = false;
            return checkbox;
          });

          return false;
        }
      }, {
        text: this.translate.instant('Ok'),
        role: 'ok',
      }, {
        text: this.translate.instant('Cancel'),
        role: 'cancel',
      }
    ];

    // adjust button order in four button layout for ios
    if (this.platform.is('ios')) {
      const okButton = { ...buttons[2] };
      const cancelButton = { ...buttons[3] };
      buttons = [buttons[0], buttons[1], cancelButton, okButton];
    }

    const alert = await this.alertController.create({
      header: header,
      inputs: options,
      cssClass: 'four-button-alert',
      buttons: [...buttons]
    });

    await alert.present();

    return await alert.onDidDismiss();
  }
}
